/**
 * A question on the part of the text just read. With a Gemini key the tutor writes a
 * meaning question (who / what / why), cached per lesson on this device; without a key,
 * offline or on any error the deterministic «что было дальше?» exercise is used.
 */
import { useEffect, useState } from 'react';
import type { Lesson } from '../../core/content.ts';
import { langCode } from '../../core/content.ts';
import type { ChoiceExercise } from '../../core/exercises.ts';
import { parseQuestion, questionPrompt } from '../../core/ai-tasks.ts';
import { askJson, getGeminiKey } from '../../data/gemini.ts';
import { readString, writeString } from '../../data/storage.ts';
import { RoboCat } from '../../ui/RoboCat.tsx';
import { ExerciseStep } from './ExerciseStep.tsx';

const CACHE_PREFIX = 'soile.aiq.v1.';
/** Waiting longer than this for the tutor is worse than the built-in question. */
const AI_TIMEOUT_MS = 7000;
const INSTRUCTION = 'Вопрос по смыслу';

export function QuestionStep({
  lesson,
  lessonId,
  questionId,
  textId,
  sentenceIds,
  fallback,
  speechLang,
  onAnswered,
  onDone,
}: {
  lesson: Lesson;
  lessonId: string;
  questionId: string;
  textId: string;
  sentenceIds: readonly string[];
  fallback: ChoiceExercise;
  speechLang: string;
  onAnswered: (correct: boolean) => number;
  onDone: () => void;
}) {
  const cacheKey = `${CACHE_PREFIX}${lessonId}.${questionId}`;
  const [exercise, setExercise] = useState<ChoiceExercise | null>(() => {
    const cached = readString(cacheKey);
    const parsed = cached ? parseQuestion(cached, questionId, INSTRUCTION) : null;
    return parsed ?? (getGeminiKey() ? null : fallback);
  });

  useEffect(() => {
    if (exercise) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), AI_TIMEOUT_MS);
    const text = lesson.texts.find((t) => t.id === textId);
    const part = (text?.sentences ?? []).filter((s) => sentenceIds.includes(s.id));
    askJson(questionPrompt(langCode(lesson.targetLang), lesson.title.text, part), controller.signal)
      .then((raw) => {
        const parsed = parseQuestion(raw, questionId, INSTRUCTION);
        if (parsed) writeString(cacheKey, raw);
        setExercise(parsed ?? fallback);
      })
      .catch(() => setExercise(fallback))
      .finally(() => window.clearTimeout(timer));
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [exercise, lesson, textId, sentenceIds, questionId, cacheKey, fallback]);

  if (!exercise) {
    return (
      <div className="center-screen center-screen--inline">
        <div className="stack">
          <RoboCat size={72} state="thinking" />
          <p className="t-meta">Учитель придумывает вопрос…</p>
        </div>
      </div>
    );
  }
  return (
    <ExerciseStep
      lesson={lesson}
      exercise={exercise}
      speechLang={speechLang}
      onAnswered={onAnswered}
      onDone={onDone}
    />
  );
}
